/**
 * wilds.ts — deterministic creature dens + mission boards (Approach A).
 *
 * MIRROR of server/src/lib/wilds.ts (headers differ; keep the math in sync —
 * the settlements law). Dens and mission boards derive from the seed + site
 * list, so every client renders the identical wilds and the server never
 * stores board rows — only creatures (live combat state) and mission HOLDS.
 *
 * Better-than-SWG by design: dens are BIOME-cast around real settlements, the
 * cull board of a town points at the dens actually menacing it, deliveries
 * run between NAMED settlements, surveys visit real wilderness waypoints, and
 * every completion pays settlement STANDING as well as SOV — the civic loop
 * SWG's static terminals never closed.
 */
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { forestDensity } from './forest_field.js';
import { commonsSeaLevel } from './sea_level.js';

export interface SpeciesSpec {
  hp: number;
  dmg: number;
  speed: number;      // m/s
  aggroM: number;
  lootSov: number;
  biomes: string[];
}

export const SPECIES: Record<string, SpeciesSpec> = {
  ridge_stalker: { hp: 60, dmg: 8, speed: 3.4, aggroM: 30, lootSov: 6, biomes: ['rocky_highlands', 'regolith_plains', 'crystal_forest'] },
  spore_drone: { hp: 30, dmg: 5, speed: 4.2, aggroM: 26, lootSov: 4, biomes: ['fungal_basin', 'moss_wetland', 'crystal_forest'] },
  duneback_brute: { hp: 140, dmg: 16, speed: 2.4, aggroM: 20, lootSov: 12, biomes: ['dune_desert', 'regolith_plains', 'rocky_highlands'] },
  // Ground-combat wave (2026-08-13): three more hunters so every biome has teeth. (Mirror drift found
  // by verify:forest 2026-09-07 — the client cast dens with the old three-species table.)
  mire_lurker: { hp: 90, dmg: 12, speed: 4.6, aggroM: 34, lootSov: 9, biomes: ['moss_wetland', 'fungal_basin'] },
  crag_wyrm: { hp: 210, dmg: 22, speed: 2.0, aggroM: 24, lootSov: 18, biomes: ['rocky_highlands', 'dune_desert'] },
  glass_shrike: { hp: 45, dmg: 10, speed: 5.2, aggroM: 36, lootSov: 8, biomes: ['crystal_forest', 'regolith_plains'] },
};

export const DEN_POP: Record<string, number> = { ridge_stalker: 3, spore_drone: 4, duneback_brute: 2, mire_lurker: 3, crag_wyrm: 2, glass_shrike: 4 };
export const CREATURE_RESPAWN_S = 240;
export const STRIKE_RANGE_M = 7;
export const STRIKE_COOLDOWN_MS = 700;
export const STRIKE_DMG = 22;
export const PLAYER_MAX_HP = 100;
export const MISSION_EPOCH_S = 6 * 3600; // the board refreshes with the season cadence
export const MISSION_SLOTS = 6;
export const TERMINAL_RANGE_M = 260;     // work is taken at the settlement's terminal

function h01(seed: number | string, salt: string): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff;
}

export interface DenSpec {
  key: string;          // "den:<siteId>:<i>"
  siteId: number;       // the settlement this den menaces
  species: string;
  x: number;
  z: number;
}

/** Species cast by the site's biome (first species listing it; stalker default). */
export function speciesForBiome(biome: string, r: number): string {
  const fits = Object.entries(SPECIES).filter(([, s]) => s.biomes.includes(biome)).map(([k]) => k);
  if (fits.length === 0) return 'ridge_stalker';
  return fits[Math.floor(r * fits.length) % fits.length];
}

const DENS_PER_TIER: Record<string, number> = { city: 3, town: 2, hamlet: 1, capital: 0, district: 0, circuit: 0 }; // capital wave: the den ring (420–980 m) would fall INSIDE a capital — its dens ring the wall in phase 3
/** Extra dens a settlement's surrounding woods may hold (the woods are where the creatures are). */
const WOODS_DENS_PER_SITE = 2;

/** Every den in the wilds — deterministic over the site list. */
export function densFor(seed: string, sites?: SettlementSite[]): DenSpec[] {
  const all = sites ?? settlementSites(seed);
  const sea = commonsSeaLevel(seed);
  const out: DenSpec[] = [];
  for (const s of all) {
    const n = DENS_PER_TIER[s.tier] ?? 1;
    for (let i = 0; i < n; i++) {
      const a = h01(s.siteSeed, `dena${i}`) * Math.PI * 2;
      const r = 340 + h01(s.siteSeed, `denr${i}`) * 300; // 340–640 m out — the approach roads
      const x = s.centerX + Math.cos(a) * r;
      const z = s.centerZ + Math.sin(a) * r;
      // Never inside ANY settlement's core.
      if (all.some((o) => Math.hypot(o.centerX - x, o.centerZ - z) < 300)) continue;
      out.push({ key: `den:${s.id}:${i}`, siteId: s.id, species: speciesForBiome(s.biome, h01(s.siteSeed, `dens${i}`)), x, z });
    }
    // WOODS DENS (flora update 2026-09-07): the forests around a settlement hold more creatures — up to
    // WOODS_DENS_PER_SITE extra dens cast INTO the woods (forest_field ≥ 0.6) on a ring 420–980 m out,
    // scanned deterministically; a site with no woods in reach gets none. Mirrored on both sides.
    let woods = 0;
    for (let i = 0; i < 20 && woods < WOODS_DENS_PER_SITE; i++) {
      const a = h01(s.siteSeed, `wooda${i}`) * Math.PI * 2;
      const r = 420 + h01(s.siteSeed, `woodr${i}`) * 560;
      const x = s.centerX + Math.cos(a) * r;
      const z = s.centerZ + Math.sin(a) * r;
      if (forestDensity(seed, x, z, sea) < 0.6) continue;
      if (all.some((o) => Math.hypot(o.centerX - x, o.centerZ - z) < 300)) continue;
      out.push({ key: `den:${s.id}:w${woods}`, siteId: s.id, species: speciesForBiome(s.biome, h01(s.siteSeed, `woods${i}`)), x, z });
      woods++;
    }
  }
  return out;
}

export type MissionKind = 'cull' | 'delivery' | 'survey';
export interface MissionSpec {
  sid: number;          // board settlement (raw site id)
  slot: number;
  epoch: number;
  kind: MissionKind;
  species: string;      // cull
  targetSiteId: number; // delivery
  wayX: number;         // survey
  wayZ: number;
  needed: number;
  rewardSov: number;
  standing: number;
}

export function missionEpoch(nowMs: number): number {
  return Math.floor(nowMs / 1000 / MISSION_EPOCH_S);
}

/** The 6-slot board of a settlement for an epoch — pure. */
export function missionsFor(seed: string, sid: number, epoch: number, sites?: SettlementSite[]): MissionSpec[] {
  const all = sites ?? settlementSites(seed);
  const site = all.find((s) => s.id === sid);
  if (!site) return [];
  const dens = densFor(seed, all).filter((d) => d.siteId === sid);
  const towns = all.filter((s) => (s.tier === 'city' || s.tier === 'town') && s.id !== sid);
  const out: MissionSpec[] = [];
  for (let slot = 0; slot < MISSION_SLOTS; slot++) {
    const r = (salt: string): number => h01(site.siteSeed, `m${epoch}:${slot}:${salt}`);
    const kindRoll = r('kind');
    if (kindRoll < 0.45 && dens.length > 0) {
      const den = dens[Math.floor(r('den') * dens.length) % dens.length];
      const needed = 3 + Math.floor(r('n') * 3); // 3–5
      out.push({
        sid, slot, epoch, kind: 'cull', species: den.species, targetSiteId: -1,
        wayX: den.x, wayZ: den.z, needed,
        rewardSov: 24 + needed * 6 + (den.species === 'duneback_brute' ? 18 : 0),
        standing: 3,
      });
    } else if (kindRoll < 0.75 && towns.length > 0) {
      const t = towns[Math.floor(r('t') * towns.length) % towns.length];
      const dist = Math.hypot(t.centerX - site.centerX, t.centerZ - site.centerZ);
      out.push({
        sid, slot, epoch, kind: 'delivery', species: '', targetSiteId: t.id,
        wayX: t.centerX, wayZ: t.centerZ, needed: 1,
        rewardSov: 12 + Math.min(40, Math.round(dist / 1200)),
        standing: 2,
      });
    } else {
      const a = r('wa') * Math.PI * 2;
      const wr = 420 + r('wr') * 480;
      out.push({
        sid, slot, epoch, kind: 'survey', species: '', targetSiteId: -1,
        wayX: site.centerX + Math.cos(a) * wr, wayZ: site.centerZ + Math.sin(a) * wr,
        needed: 1, rewardSov: 16, standing: 2,
      });
    }
  }
  return out;
}
